import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { ButtonDirective, ButtonIcon } from 'primeng/button';
import { forkJoin, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  take,
} from 'rxjs/operators';

import { BD_GLOBAL_SEARCH_RESULTS_ROUTE } from './global-search-routes';
import {
  BD_SEARCH_PROVIDERS,
  BdSearchProvider,
  BdSearchResult,
} from './search';

/** Default page shown when the user asks to see every match for a query. */
const DEFAULT_SEARCH_RESULTS_ROUTE = ['/search'];

/**
 * Idle time (ms) the input must stay unchanged before the providers are hit.
 * Keeps a fast typist from firing a request per keystroke; `switchMap` then
 * discards any request still in flight once a newer query supersedes it.
 */
const SEARCH_DEBOUNCE_MS = 300;

/** Shortest query that is sent to the providers. */
const MIN_QUERY_LENGTH = 2;

@Component({
  selector: 'bd-global-search',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block w-full max-w-2xl mx-auto min-w-0',
  },
  template: `
    <!-- The shareables search needs an authenticated session, so the entire
         search bar (inline + mobile trigger/overlay) is gated behind sign-in. -->
    @if (isAuthenticated()) {
      <!-- Mobile-only trigger: collapsed icon, expands to overlay -->
      @if (!mobileExpanded()) {
        <button
          type="button"
          class="bd-global-search-trigger"
          (click)="expandMobile()"
          aria-label="Suche öffnen"
          data-test="global-search-mobile-trigger"
        >
          <i class="pi pi-search"></i>
        </button>
      }

      <!-- Search group: inline on >=lg, overlay on <lg when expanded.
           Below lg the trigger icon is shown and the bar opens as a fixed
           full-toolbar overlay so tablet (768px) and phone widths get usable
           space for the right-hand actions instead of a squeezed inline bar. -->
      <div
        class="bd-global-search-group hidden lg:flex items-center w-full gap-1"
        [class.is-mobile-overlay]="mobileExpanded()"
      >
        @if (mobileExpanded()) {
          <button
            type="button"
            class="bd-global-search-close"
            (click)="collapseMobile()"
            aria-label="Suche schließen"
            data-test="global-search-mobile-close"
          >
            <i class="pi pi-arrow-left"></i>
          </button>
        }

        <p-autocomplete
          class="bd-global-search-autocomplete"
          inputStyleClass="bd-global-search-input"
          [suggestions]="results()"
          [virtualScroll]="true"
          [virtualScrollItemSize]="SUGGESTION_ITEM_SIZE"
          [dropdown]="false"
          [forceSelection]="false"
          [showEmptyMessage]="true"
          optionLabel="title"
          emptyMessage="Keine Vorschläge"
          placeholder="Suche nach Ressourcen…"
          ariaLabel="Suche nach Ressourcen…"
          appendTo="body"
          (completeMethod)="onSearch($event)"
          (onSelect)="onSelect($event)"
          (keydown.enter)="onEnter()"
        >
          <!-- Two lines per option: the resource name, then its facts — type,
               one glyph per granted permission, and the last change (which is
               also the sort key, so it explains the row order).

               Fixed-height row: h-[60px] mirrors SUGGESTION_ITEM_SIZE so the
               virtual scroller measures every row identically; overflow-hidden
               keeps a tag-heavy row from desyncing the scroll calculation.
               No leading icon: the glyph was constant per result kind, so it
               carried nothing the type label doesn't. -->
          <ng-template let-result #item>
            <div
              class="flex flex-col justify-center gap-1 h-[60px] overflow-hidden"
            >
              <!-- font-semibold (600) matches the clinic browser's option row
                   and the result page's row title — the styleguide's
                   "row / card title" weight. -->
              <span class="truncate font-semibold">{{ result.title }}</span>
              <span
                class="flex items-center gap-1.5 min-w-0 overflow-hidden text-xs text-(--p-text-muted-color)"
              >
                @if (result.typeLabel) {
                  <span class="font-medium whitespace-nowrap">{{
                    result.typeLabel
                  }}</span>
                }
                @if (result.description) {
                  <span aria-hidden="true">·</span>
                  <span class="truncate">{{ result.description }}</span>
                }
                @if (result.modifiedDate) {
                  <span aria-hidden="true">·</span>
                  <span class="shrink-0 whitespace-nowrap">{{
                    result.modifiedDate | date: 'dd.MM.yyyy'
                  }}</span>
                }
              </span>
            </div>
          </ng-template>
          <!-- In-app navigation to the result page = styleguide category 02
               "Action": filled secondary. A text button with no severity
               renders the label in brand orange, which reads as a primary CTA
               and is hard to read on the panel surface. -->
          <!-- Custom loading icon: PrimeNG's default loader inherits colour, and
               inside the dark app-shell toolbar it inherits the shell's white
               contrast colour onto the white input — an invisible spinner. Owning
               the element lets the colour be set inline, which no cascade or
               view-encapsulation boundary can defeat. -->
          <ng-template #loadingicon>
            <i
              class="pi pi-spinner pi-spin"
              style="color: var(--p-text-muted-color)"
              aria-hidden="true"
            ></i>
          </ng-template>
          <!-- Panel width comes from a min-width, so a percentage width on a
               child collapses to shrink-to-fit — that is what wrapped this label
               onto four clipped lines. self-stretch takes the panel's full cross
               size instead, and whitespace-nowrap keeps the label on one line
               even where it doesn't. -->
          <ng-template #footer>
            @if (query().length >= 2) {
              <div
                class="flex self-stretch p-2 border-t border-(--p-content-border-color)"
              >
                <button
                  pButton
                  type="button"
                  class="flex-1 justify-start whitespace-nowrap"
                  severity="secondary"
                  [text]="true"
                  (click)="showAllResults()"
                  data-test="global-search-show-all"
                >
                  <i class="pi pi-search mr-2" pButtonIcon></i>
                  Alle Ergebnisse für „{{ query() }}“ anzeigen
                </button>
              </div>
            }
          </ng-template>
        </p-autocomplete>
      </div>
    }
  `,
  styles: [
    `
      :host {
        position: relative;
      }

      /* Slim, wide input — match the toolbar's compact 54px height. */
      :host ::ng-deep .bd-global-search-autocomplete {
        display: flex;
        flex: 1 1 auto;
        min-width: 0;
        width: 100%;
      }

      :host ::ng-deep .bd-global-search-autocomplete .p-autocomplete-input {
        width: 100%;
        padding-block: 0.375rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
      }

      /* Note: the suggestion panel is appendTo="body", i.e. outside this
         component's DOM subtree, so :host-scoped rules cannot reach it. Panel
         content is styled with Tailwind utilities and inline styles in the
         templates above instead. */

      /* Mobile/tablet icon trigger: matches the toolbar buttons' visual
         weight. Hidden on >=lg via a scoped media query — keeps Tailwind
         utilities out of the picture so component CSS specificity stays
         predictable. The 1024px breakpoints below mirror Tailwind's "lg"
         (see tailwind defaults); CSS media queries can't read CSS custom
         properties, so the value is duplicated rather than tokenised. */
      .bd-global-search-trigger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        border-radius: 6px;
        background: transparent;
        color: var(--app-shell-contrast-color);
        border: 1px solid transparent;
        cursor: pointer;
        padding: 0;
        font-size: 1.125rem;
      }

      .bd-global-search-trigger:hover {
        background: rgba(255, 255, 255, 0.12);
      }

      @media (min-width: 1024px) {
        .bd-global-search-trigger {
          display: none;
        }
      }

      /* Mobile back/close button inside the overlay. */
      .bd-global-search-close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        border-radius: 6px;
        background: transparent;
        color: var(--app-shell-contrast-color);
        border: 1px solid transparent;
        cursor: pointer;
        padding: 0;
        flex: 0 0 auto;
        font-size: 1.125rem;
      }

      .bd-global-search-close:hover {
        background: rgba(255, 255, 255, 0.12);
      }

      @media (min-width: 1024px) {
        .bd-global-search-close {
          display: none;
        }
      }

      /* Mobile/tablet overlay: scope to <lg so we don't leak overlay styles
         onto desktop where mobileExpanded would never normally toggle but
         the class binding may still be present. */
      @media (max-width: 1023px) {
        .bd-global-search-group.is-mobile-overlay {
          display: flex !important;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 60;
          height: 54px;
          padding: 0 0.5rem;
          background: var(--app-shell-color);
          color: var(--app-shell-contrast-color);
        }
      }
    `,
  ],
  imports: [AutoComplete, ButtonDirective, ButtonIcon, DatePipe],
})
export class BdGlobalSearchComponent {
  /**
   * Fixed pixel height of one suggestion row; must stay in lockstep with the
   * `h-[60px]` on the `#item` template — the virtual scroller positions rows
   * from this number, so a mismatch desyncs the scroll offset.
   */
  protected readonly SUGGESTION_ITEM_SIZE = 60;

  public readonly results = signal<BdSearchResult[]>([]);
  public readonly query = signal('');
  public readonly mobileExpanded = signal(false);
  public readonly autoComplete = viewChild(AutoComplete);

  private readonly _providers =
    inject<readonly BdSearchProvider[] | null>(BD_SEARCH_PROVIDERS, {
      optional: true,
    }) ?? [];
  private readonly _router = inject(Router);
  private readonly _searchResultsRoute =
    inject(BD_GLOBAL_SEARCH_RESULTS_ROUTE, { optional: true }) ??
    DEFAULT_SEARCH_RESULTS_ROUTE;
  private readonly _authService = inject(BdAuthenticationService);

  /**
   * The shareables search requires authentication, so the whole search bar is
   * only rendered once the user is signed in. `authentication$` is backed by a
   * BehaviorSubject(false), so this is `false` until MSAL confirms a session.
   */
  protected readonly isAuthenticated = toSignal(
    this._authService.authentication$,
    { initialValue: false },
  );

  /** Raw query terms emitted on every keystroke, before debounce/cancellation. */
  private readonly _searchTerms = new Subject<string>();

  constructor() {
    this._searchTerms
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        // switchMap cancels the previous provider fan-out as soon as a newer
        // query arrives, so a slow earlier request can never overwrite the
        // results of the query the user actually settled on.
        switchMap((query) => this._runSearch(query)),
        takeUntilDestroyed(),
      )
      .subscribe((results) => this.results.set(results));
  }

  public onSearch(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.trim() ?? '';
    // Update the signal synchronously so the "show all" footer reacts
    // immediately; only the provider request is debounced. PrimeNG raises its
    // own in-input loader for the whole span between `completeMethod` and the
    // next `[suggestions]` change, so no separate in-flight flag is needed.
    this.query.set(query);
    this._searchTerms.next(query);
  }

  /**
   * Fans out the (debounced) query across every registered provider in
   * ascending `order`, swallowing per-provider failures, and flattens the hits
   * into a single list. Short queries resolve to an empty list.
   */
  private _runSearch(query: string) {
    if (query.length < MIN_QUERY_LENGTH || this._providers.length === 0) {
      return of<BdSearchResult[]>([]);
    }

    const sortedProviders = [...this._providers].sort(
      (a, b) => a.order - b.order,
    );
    return forkJoin(
      sortedProviders.map((provider) =>
        provider.search(query).pipe(
          take(1),
          catchError(() => of<BdSearchResult[]>([])),
        ),
      ),
    ).pipe(map((lists) => lists.flat()));
  }

  public onSelect(event: AutoCompleteSelectEvent): void {
    const result = event.value as BdSearchResult;
    result.open();
    this._clearSearch();
    this.collapseMobile();
  }

  /**
   * Navigates to the dedicated search page with the current query, listing
   * every match instead of the truncated suggestion list. Triggered by the
   * inline submit button, the dropdown footer link, or pressing Enter.
   */
  public showAllResults(): void {
    const query = this.query().trim();
    if (query.length < 2) {
      return;
    }
    this._router.navigate(this._searchResultsRoute, {
      queryParams: { q: query },
    });
    this._clearSearch();
    this.collapseMobile();
  }

  /**
   * Pressing Enter inside the input submits to the results page unless the
   * user is currently picking a suggestion (PrimeNG handles selection itself).
   */
  public onEnter(): void {
    if (this.query().trim().length < 2) {
      return;
    }
    this.showAllResults();
  }

  public expandMobile(): void {
    this.mobileExpanded.set(true);
    queueMicrotask(() => this._focusInput());
  }

  public collapseMobile(): void {
    if (!this.mobileExpanded()) return;
    this.mobileExpanded.set(false);
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (this.mobileExpanded()) {
      this.collapseMobile();
      this._clearSearch();
    }
  }

  private _focusInput(): void {
    const ac = this.autoComplete() as unknown as
      | {
          inputEL?: { nativeElement?: HTMLInputElement };
        }
      | undefined;
    ac?.inputEL?.nativeElement?.focus();
  }

  private _clearSearch(): void {
    this.results.set([]);
    this.query.set('');
    this.autoComplete()?.clear();
  }
}
